import { ref } from 'vue';
import store from '@/store';
import { config } from '@/config.js';

/**
 * useResumenVentasCatalogos — catálogos de los selectores del "Resumen de
 * ventas": teatros recinto, taquilleros y compañías productoras.
 *
 * Los tres son datos de CONFIGURACIÓN de la vista (se piden una vez y no
 * dependen de los filtros), por eso van separados del composable del listado:
 * cambiar un filtro no debe volver a pedir los catálogos.
 *
 * Endpoints (v1):
 *  - `back.usuarios.teatros`            → GET /usuarios/teatros
 *  - `back.taquilleros.index`           → GET /taquilleros
 *  - `back.teatro.compania-eventos.index` → GET /teatro/compania-eventos
 *
 * @param {{
 *   fetchTeatros?: Function,
 *   fetchTaquilleros?: Function,
 *   fetchCompanias?: Function
 * }} [opciones] fetchers inyectables para tests.
 * @returns {Object} catálogos y acciones de carga.
 */
export function useResumenVentasCatalogos({ fetchTeatros, fetchTaquilleros, fetchCompanias } = {}) {
    const teatros = ref([]);
    const taquilleros = ref([]);
    const companias = ref([]);
    const loading = ref(false);
    const error = ref(null);

    const teatroActual = () => store.getters.getTeatro || {};

    async function dispatchTeatros() {
        return store.dispatch(config.getVuexAlias('back.usuarios.teatros'), { filters: {} });
    }

    async function dispatchTaquilleros() {
        return store.dispatch(config.getVuexAlias('back.taquilleros.index'), { filters: {} });
    }

    /**
     * Las compañías son las del teatro en sesión, no todas: el backend las
     * filtra por `teatro_id` y `to_select` (modo selector).
     */
    async function dispatchCompanias() {
        return store.dispatch(config.getVuexAlias('back.teatro.compania-eventos.index'), {
            filters: {},
            teatro_id: teatroActual().id,
            to_select: true
        });
    }

    const doTeatros = fetchTeatros || dispatchTeatros;
    const doTaquilleros = fetchTaquilleros || dispatchTaquilleros;
    const doCompanias = fetchCompanias || dispatchCompanias;

    /**
     * Extrae el array de un body paginado (`{ data: [...] }`) con red de
     * seguridad: un body inesperado deja el catálogo vacío en vez de romper el
     * render del select.
     */
    const comoLista = res => {
        const payload = (res && res.data) || {};

        return Array.isArray(payload.data) ? payload.data : [];
    };

    /**
     * Carga los tres catálogos en paralelo. Nunca rechaza: si un catálogo falla
     * se expone el error y el select queda vacío, pero los demás se muestran
     * igual.
     */
    async function cargarCatalogos() {
        loading.value = true;
        error.value = null;

        const [resTeatros, resTaquilleros, resCompanias] = await Promise.allSettled([
            doTeatros(),
            doTaquilleros(),
            doCompanias()
        ]);

        if (resTeatros.status === 'fulfilled') teatros.value = comoLista(resTeatros.value);
        if (resTaquilleros.status === 'fulfilled') taquilleros.value = comoLista(resTaquilleros.value);
        if (resCompanias.status === 'fulfilled') companias.value = comoLista(resCompanias.value);

        const fallidos = [resTeatros, resTaquilleros, resCompanias].filter(r => r.status === 'rejected');
        if (fallidos.length) {
            error.value = fallidos[0].reason;
            if (process.env.NODE_ENV !== 'production') console.error(fallidos[0].reason);
        }

        loading.value = false;
    }

    return {
        teatros,
        taquilleros,
        companias,
        loading,
        error,
        cargarCatalogos
    };
}
